import { Box, DialogContentText, Stack, Typography } from '@mui/material'
import { VesselVisitBaplieParseResult } from '@planning/pages/Order/stores/OrderListUploadParsingStore'
import { OrderListUploadViewStoreV2 } from '@planning/pages/Order/stores/OrderListUploadViewStoreV2'
import { IVesselVisitItem } from '@planning/rt-stores/vesselVisit/VesselVisitItem'
import { useTranslate } from '@tolgee/react'
import { ConfirmationDialog, EmptyIndicator } from '@tom-ui/ui'
import { observer } from 'mobx-react-lite'
import { FC } from 'react'

type VesselVisitMismatchLineContentProps = {
  title: string
  value: string | undefined
}

const VesselVisitMismatchLineContent: FC<VesselVisitMismatchLineContentProps> = ({
  title,
  value,
}) => {
  return (
    <Stack direction='row'>
      <Typography variant='body2'>{title}:&nbsp;</Typography>
      <Typography variant='subtitle2'>{value || EmptyIndicator}</Typography>
    </Stack>
  )
}

type VesselVisitMismatchColumnContentProps = {
  title: string | undefined
  vesselName: string | undefined
  imoNumber: string | undefined
  callSign: string | undefined
}
const VesselVisitMismatchColumnContent: FC<VesselVisitMismatchColumnContentProps> = ({
  title,
  vesselName,
  imoNumber,
  callSign,
}) => {
  const { t } = useTranslate()

  return (
    <DialogContentText>
      <Stack direction='column'>
        <Typography variant='subtitle2'>{title}</Typography>
        <VesselVisitMismatchLineContent title={t('vesselName', 'Vessel name')} value={vesselName} />
        <VesselVisitMismatchLineContent title={t('imoNumber', 'IMO')} value={imoNumber} />
        <VesselVisitMismatchLineContent title={t('callSign', 'Call sign')} value={callSign} />
      </Stack>
    </DialogContentText>
  )
}

type VesselMismatchComparissonContentProps = {
  vesselVisitFromUpdate: VesselVisitBaplieParseResult
  selectedVesselVisitItem: IVesselVisitItem
}
const VesselMismatchComparissonContent: FC<VesselMismatchComparissonContentProps> = observer(
  ({ vesselVisitFromUpdate, selectedVesselVisitItem }) => {
    const { t } = useTranslate()

    return (
      <Box sx={{ mt: '1rem', mb: '1rem' }}>
        <DialogContentText>
          <Typography variant='subtitle1'>{t('vesselDetails', 'Vessel details:')}</Typography>
        </DialogContentText>
        <Box sx={{ display: 'flex', flexDirection: 'row', gap: '2rem' }}>
          <Box sx={{ mt: '0.5rem' }}>
            <VesselVisitMismatchColumnContent
              title={`${t('uploadedVessel', 'Uploaded vessel')}:`}
              vesselName={vesselVisitFromUpdate.vesselName}
              imoNumber={vesselVisitFromUpdate.imoNumber}
              callSign={vesselVisitFromUpdate.callSign}
            />
          </Box>
          {selectedVesselVisitItem.vessels.map(vessel => (
            <Box key={vessel.data.id} sx={{ mt: '0.5rem', ml: '2rem' }}>
              <VesselVisitMismatchColumnContent
                title={`${t('selectedVessel', 'Selected vessel')}:`}
                vesselName={vessel.data.name}
                imoNumber={vessel.data.imoNumber ?? undefined}
                callSign={vessel.data.callSign}
              />
            </Box>
          ))}
        </Box>
      </Box>
    )
  },
)

export type VesselVisitMismatchDialogProps = {
  store: OrderListUploadViewStoreV2
}

export const VesselVisitMismatchDialog: FC<VesselVisitMismatchDialogProps> = observer(
  ({ store }) => {
    const { t } = useTranslate()

    const { showVesselVisitMismatchWarning, selectedVesselVisitItem } = store
    const { vesselVisitFromUpdate } = store.dataStore

    if (!showVesselVisitMismatchWarning || !vesselVisitFromUpdate || !selectedVesselVisitItem)
      return <></>

    return (
      <ConfirmationDialog
        open={showVesselVisitMismatchWarning}
        title={t('vesselVisitMismatchTitle', 'Proceed with selected vessel visit?')}
        message={t(
          'vesselVisitMismatchMessage',
          'The uploaded list belongs to a different vessel than the selected vessel visit. Verify that the correct vessel visit is selected before continuing.',
        )}
        primaryActionText={t('yesProceed', 'Yes, proceed')}
        closeLabel={t('noBackToEdit', 'No, back to edit')}
        onConfirm={() => {
          store.setShowVesselVisitMismatchWarning(false)
          store.setActiveStep(1)
        }}
        maxWidth='sm'
        onCancel={() => store.setShowVesselVisitMismatchWarning(false)}
        onClose={() => store.setShowVesselVisitMismatchWarning(false)}
        hasBorderTop
        hasTitleBorder
      >
        <VesselMismatchComparissonContent
          vesselVisitFromUpdate={vesselVisitFromUpdate}
          selectedVesselVisitItem={selectedVesselVisitItem}
        />
      </ConfirmationDialog>
    )
  },
)
